/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import isNil from 'lodash/isNil';
import Terminal, {
  DataPath,
  InstallationType,
  NetworkAvailability,
  SatelliteInFov,
  SpaceNetworkInfo,
  TerminalDetails,
  TerminalTypeLabel,
} from '../shared/entities/terminal';
import { getConnectivityColor, isActive, isStandby } from './ConnectionStatusUtils';
import { EMPTY_FIELD_STRING_PLACEHOLDER, valueOrPlaceholder } from './StringUtils';
import { kiloToMega } from './DataAmountUtils';
import { bpsToUnitString } from './DataRateUtils';

const MAX_ALTERNATIVE_SATELLITES_IN_FOV_TO_SHOW = 3;
export const EMPTY_TAB_INDEX_SET_BY_FTV = -1;
export const DEFAULT_TAB = 0;
export const CWS1_TAB = 0;
export const CWS2_TAB = 1;
export const OVERVIEW_TAB = 0;
export const KA_TAB = 1;
export const KU_TAB = 2;
export const VI_TAB = 0;
export const RTE_TAB = 1;


  const SECONDS_PER_MINUTE = 60;
  const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
  const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;

  export const isTerminalInBeamPositionInfoBlankedAndVisibilityOnMapDisabled = (terminal: Terminal) => {
    // TODO: Implement the logic to check the conditions if the terminal's position info should be blanked an the ability to show it on the map disabled
    return false;
  };

  export const getTerminalDisplayName = (terminal: any) => {
    return (terminal || {}).terminalDisplayName || EMPTY_FIELD_STRING_PLACEHOLDER;
  };

  export const terminalTypeDisplayNameFromTerminalTypeLabel = (
    terminalType: TerminalTypeLabel | undefined,
  ): string | undefined => {
    switch (terminalType) {
      case TerminalTypeLabel.CWS1:
        return 'CWS1';
      case TerminalTypeLabel.CWS2:
        return 'CWS2';
      case TerminalTypeLabel.KUKA:
        return 'KuKa';
      case TerminalTypeLabel.KA:
        return 'Ka';
      case TerminalTypeLabel.KU:
        return 'Ku';
      case TerminalTypeLabel.MMT:
        return 'MMT';
      case TerminalTypeLabel.MRT:
        return 'MRT';
      case TerminalTypeLabel.TT:
        return 'TT';
      case TerminalTypeLabel.VI:
        return 'VI';
      case TerminalTypeLabel.RTE:
        return 'RTE';
      default:
        return undefined;
    }
  };

  export const terminalTypeSortPriorityFromTerminalTypeLabel = (
    terminalType: TerminalTypeLabel | undefined,
  ): number => {
    switch (terminalType) {
      case TerminalTypeLabel.CWS1:
        return 0;
      case TerminalTypeLabel.CWS2:
        return 1;
      case TerminalTypeLabel.KUKA:
        return 0;
      case TerminalTypeLabel.KA:
        return 0;
      case TerminalTypeLabel.KU:
        return 1;
      case TerminalTypeLabel.MMT:
        return 0;
      case TerminalTypeLabel.VI:
        return 0;
      case TerminalTypeLabel.RTE:
        return 1;
      default:
        return 0;
    }
  };

  const getTerminalSatellites = (terminal: TerminalDetails, isSatellitesInFOVEnabled: boolean) => {
    const altSatellites =
      isSatellitesInFOVEnabled &&
      !isNil(terminal.satellitesInFOV) &&
      terminal.satellitesInFOV.length > 0 &&
      networkNameToDataPath(terminal.networkOrigin) === DataPath.KU
        ? terminal.satellitesInFOV
        : [];
    // Truncate and enrich alternative satellites
    const satellites = altSatellites
      .slice(0, MAX_ALTERNATIVE_SATELLITES_IN_FOV_TO_SHOW)
      .map((satelliteInFov: SatelliteInFov) => {
        satelliteInFov.dataPath = networkNameToDataPath(terminal.networkOrigin);
        satelliteInFov.connectivityStatus = terminal.connectivityStatus;
        satelliteInFov.link = terminal.rlWaveform;
        satelliteInFov.isAlternative = true;
        return satelliteInFov;
      });
    // Enrich and add current satellite to array
    if (terminal.beamName && terminal.beamDisplayName) {
      satellites.splice(0, 0, {
        ...terminal,
        satelliteName: valueOrPlaceholder(terminal.beamDisplayName),
        dataPath: networkNameToDataPath(terminal.networkOrigin),
        link: terminal.rlWaveform,
        isAlternative: false,
      });
    }

    return satellites;
  };

  export const getTerminalSatelliteList = (
    terminal: TerminalDetails | undefined,
    isOverviewMode: boolean,
    isSatellitesInFOVEnabled: boolean,
  ) => {
    let satellites: SatelliteInFov[];

    if (isNil(terminal)) {
      return [];
    }

    if (isOverviewMode && terminal.terminalsOnAircraft && terminal.terminalsOnAircraft.length > 0) {
      if (terminal.terminalTypeLabel === TerminalTypeLabel.KA) {
        let kaSatellites = getTerminalSatellites(terminal, isSatellitesInFOVEnabled);
        let kuSatellites = getTerminalSatellites(terminal.terminalsOnAircraft[0], isSatellitesInFOVEnabled);
        satellites = kaSatellites.concat(kuSatellites);
      } else {
        let kaSatellites = getTerminalSatellites(terminal.terminalsOnAircraft[0], isSatellitesInFOVEnabled);
        let kuSatellites = getTerminalSatellites(terminal, isSatellitesInFOVEnabled);
        satellites = kaSatellites.concat(kuSatellites);
      }
    } else {
      satellites = getTerminalSatellites(terminal, isSatellitesInFOVEnabled);
    }

    return satellites;
  };

  export const getSatelliteStatusColor = (satellite: SatelliteInFov) => {
    return !satellite.isAlternative ? getConnectivityColor(satellite.connectivityStatus) : 'white';
  };

  export const networkNameToDataPath = (rawNetworkName: string) => {
    if (!isNil(rawNetworkName)) {
      const lowercaseNetwork = rawNetworkName.toLocaleLowerCase();
      if (lowercaseNetwork === 'arclight-1') {
        return DataPath.KU;
      } else if (lowercaseNetwork === 'viasat' || lowercaseNetwork === 'viasat-3') {
        return DataPath.KA;
      }
    }
    return undefined;
  };

  export const getOtherTerminalOnAircraft = (terminal: TerminalDetails | undefined) => {
    if (
      !isNil(terminal) &&
      !isNil(terminal.terminalsOnAircraft) &&
      terminal.terminalsOnAircraft.length > 0 &&
      !isNil(terminal.terminalsOnAircraft[0])
    ) {
      return terminal.terminalsOnAircraft[0];
    } else {
      return {} as TerminalDetails;
    }
  };

  export const getKaTerminalOnAircraft = (terminal: TerminalDetails | undefined) => {
    return !isNil(terminal) && terminal.terminalTypeLabel === TerminalTypeLabel.KA
      ? terminal || ({} as TerminalDetails)
      : getOtherTerminalOnAircraft(terminal);
  };

  export const getKuTerminalOnAircraft = (terminal: TerminalDetails | undefined) => {
    return !isNil(terminal) && terminal.terminalTypeLabel === TerminalTypeLabel.KU
      ? terminal || ({} as TerminalDetails)
      : getOtherTerminalOnAircraft(terminal);
  };

  export const getCWS1TerminalOnAircraft = (terminal: TerminalDetails | undefined) => {
    return !isNil(terminal) && terminal.terminalTypeLabel === TerminalTypeLabel.CWS1
      ? terminal || ({} as TerminalDetails)
      : getOtherTerminalOnAircraft(terminal);
  };

  export const getCWS2TerminalOnAircraft = (terminal: TerminalDetails | undefined) => {
    return !isNil(terminal) && terminal.terminalTypeLabel === TerminalTypeLabel.CWS2
      ? terminal || ({} as TerminalDetails)
      : getOtherTerminalOnAircraft(terminal);
  };

  export const getViTerminalOnAircraft = (terminal: TerminalDetails | undefined) => {
    return !isNil(terminal) && terminal.terminalTypeLabel === TerminalTypeLabel.VI
      ? terminal || ({} as TerminalDetails)
      : getOtherTerminalOnAircraft(terminal);
  };

  export const getRteTerminalOnAircraft = (terminal: TerminalDetails | undefined) => {
    return !isNil(terminal) && terminal.terminalTypeLabel === TerminalTypeLabel.RTE
      ? terminal || ({} as TerminalDetails)
      : getOtherTerminalOnAircraft(terminal);
  };

  const toNetworkAvailability = (connectivityStatus: string | undefined): NetworkAvailability => {
    if (isActive(connectivityStatus)) {
      return NetworkAvailability.AVAILABLE;
    }
    return isStandby(connectivityStatus) ? NetworkAvailability.UNAVAILABLE : NetworkAvailability.OFFLINE;
  };

  const formatDataRate = (bps: number | null | undefined) =>
    isNil(bps) ? EMPTY_FIELD_STRING_PLACEHOLDER : bpsToUnitString(bps, 0);

  type HaloNetDatapath = Pick<Terminal, 'terminalTypeLabel' | 'connectivityStatus' | 'flDataRateBps' | 'rlDataRateBps'>;

  // Rows are in VI_TAB/RTE_TAB order. A datapath with no record counts as offline.
  export const getHaloNetSpaceNetworks = (
    terminal: (HaloNetDatapath & Pick<Terminal, 'installationType'>) | undefined,
    siblingTerminal: HaloNetDatapath | undefined,
  ): SpaceNetworkInfo[] => {
    if (isNil(terminal) || terminal.installationType !== InstallationType.HALONET_TERMINAL) {
      return [];
    }
    return [TerminalTypeLabel.VI, TerminalTypeLabel.RTE].map((terminalTypeLabel: TerminalTypeLabel) => {
      const datapath = [terminal, siblingTerminal].find(
        candidate => candidate?.terminalTypeLabel === terminalTypeLabel,
      );
      return {
        networkName: terminalTypeDisplayNameFromTerminalTypeLabel(terminalTypeLabel) as string,
        networkAvailability: toNetworkAvailability(datapath?.connectivityStatus),
        networkMetricText:
          terminalTypeLabel === TerminalTypeLabel.VI && datapath
            ? `FL ${formatDataRate(datapath.flDataRateBps)} / RL ${formatDataRate(datapath.rlDataRateBps)}`
            : EMPTY_FIELD_STRING_PLACEHOLDER,
      };
    });
  };

  export const getTerminalDetailsBasedOnTerminalType = (
    terminal: TerminalDetails | undefined,
    terminalType: TerminalTypeLabel | undefined,
  ): TerminalDetails => {
    return !isNil(terminal) && terminal.terminalTypeLabel === terminalType
      ? terminal
      : getOtherTerminalOnAircraft(terminal);
  };

  /**
   * Compare two terminal details to see if they are the same terminal.
   *
   * The Ku + Ka configuration is considered to be "one" terminal.
   * The dual terminal configuration is considered to be "two" terminals.
   * @param terminal1
   * @param terminal2
   * @return true if both terminals are considered to be the same, false if not
   */
  export const areTheSameTerminal = (
    terminal1: TerminalDetails | undefined,
    terminal2: TerminalDetails | undefined,
  ) => {
    if (
      !isNil(terminal1) &&
      !isNil(terminal1.datapathUuid) &&
      !isNil(terminal2) &&
      !isNil(terminal2.datapathUuid) &&
      terminal1.installationType === terminal2.installationType
    ) {
      switch (terminal1.installationType) {
        case InstallationType.SINGLE_TERMINAL:
          return terminal1.datapathUuid === terminal2.datapathUuid;
        case InstallationType.DUAL_TERMINAL:
          return terminal1.datapathUuid === terminal2.datapathUuid;
        case InstallationType.KU_PLUS_KA:
          return terminal1.datapathUuid === terminal2.datapathUuid
            ? true
            : terminal1.datapathUuid === getOtherTerminalOnAircraft(terminal2).datapathUuid &&
                terminal2.datapathUuid === getOtherTerminalOnAircraft(terminal1).datapathUuid;
        default:
          return false;
      }
    } else {
      return false;
    }
  };

  /**
   * Based on the terminal tab selection, get the associated
   * terminal type label with that tab.
   * @param selectedTabIndex - index of selected terminal tab
   * @param terminal - selected terminal details
   */
  export const getSelectedTerminalTypeLabelBasedOnTab = (
    selectedTabIndex: number | undefined,
    selectedTerminal: Terminal | undefined,
  ) => {
    if (isNil(selectedTerminal)) {
      return undefined;
    }
    const tabIndex = Math.max(selectedTabIndex || DEFAULT_TAB, 0);
    switch (selectedTerminal.installationType) {
      case InstallationType.SINGLE_TERMINAL:
        return selectedTerminal.terminalTypeLabel;
      case InstallationType.DUAL_TERMINAL:
        return tabIndex === CWS1_TAB ? TerminalTypeLabel.CWS1 : TerminalTypeLabel.CWS2;
      case InstallationType.KU_PLUS_KA:
        if (tabIndex === OVERVIEW_TAB) {
          return undefined;
        }
        return tabIndex === KA_TAB ? TerminalTypeLabel.KA : TerminalTypeLabel.KU;
      case InstallationType.HALONET_TERMINAL:
        return tabIndex === VI_TAB ? TerminalTypeLabel.VI : TerminalTypeLabel.RTE;
      default:
        return undefined;
    }
  };

  /**
   * Given a selected terminal, determine which tab should be selected.
   * @param selectedTerminal
   */
  export const getSelectedTabBasedOnTerminalTypeLabel = (selectedTerminal: Terminal | undefined) => {
    if (!isNil(selectedTerminal)) {
      const installationType = selectedTerminal.installationType;
      const terminalTypeLabel = selectedTerminal.terminalTypeLabel;
      switch (installationType) {
        case InstallationType.SINGLE_TERMINAL:
          return undefined;
        case InstallationType.DUAL_TERMINAL:
          return terminalTypeLabel === TerminalTypeLabel.CWS1 ? CWS1_TAB : CWS2_TAB;
        case InstallationType.KU_PLUS_KA:
          if (terminalTypeLabel === TerminalTypeLabel.KA) {
            return KA_TAB;
          } else if (terminalTypeLabel === TerminalTypeLabel.KU) {
            return KU_TAB;
          } else {
            return OVERVIEW_TAB;
          }
        case InstallationType.HALONET_TERMINAL:
          return terminalTypeLabel === TerminalTypeLabel.RTE ? RTE_TAB : VI_TAB;
        default:
          return DEFAULT_TAB;
      }
    } else {
      return DEFAULT_TAB;
    }
  };

  /**
   * Build the list of terminals in beam
   * based on the currently selected tab.
   *
   * @param terminal - terminal details
   * @param selectedTerminalTabIndex - selected tab
   */
  export const buildTerminalsInBeamList = (
    terminal: TerminalDetails | undefined,
    selectedTerminalTabIndex: number | undefined,
  ) => {
    const terminalTypeLabel = (terminal || {}).terminalTypeLabel;
    const installationType = (terminal || {}).installationType;
    const selectedTerminalType = getSelectedTerminalTypeLabelBasedOnTab(selectedTerminalTabIndex, terminal);

    const terminalsInBeam = (terminal || {}).terminalsInBeam || [];
    const otherTerminalsInBeam = getOtherTerminalOnAircraft(terminal).terminalsInBeam || [];

    switch (installationType) {
      case InstallationType.KU_PLUS_KA:
        if (!isNil(selectedTerminalType)) {
          return selectedTerminalType === terminalTypeLabel ? terminalsInBeam : otherTerminalsInBeam;
        } else {
          return [...terminalsInBeam, ...otherTerminalsInBeam];
        }
      case InstallationType.DUAL_TERMINAL:
        return selectedTerminalType === terminalTypeLabel ? terminalsInBeam : otherTerminalsInBeam;
      default:
        return terminalsInBeam;
    }
  };

  /**
   * Determines whether or not to use the other terminal on aircraft
   * data for updating state variables such as the flight path.
   *
   * @param terminalDetails - terminal data
   * @param selectedDatapathUuidFleetView - selected terminal on fleet view
   * @param selectedTerminalTabIndex - selected tab on the UI
   */
  export const shouldUseOtherTerminalOnAircraftForUpdate = (
    terminalDetails: TerminalDetails,
    selectedDatapathUuidFleetView: string | undefined,
    selectedTerminalTabIndex: number | undefined,
  ) => {
    return (
      terminalDetails.installationType === InstallationType.DUAL_TERMINAL &&
      selectedDatapathUuidFleetView === terminalDetails.datapathUuid &&
      getSelectedTerminalTypeLabelBasedOnTab(selectedTerminalTabIndex, terminalDetails) ===
        getOtherTerminalOnAircraft(terminalDetails).terminalTypeLabel
    );
  };
  /**
   * if satellite/antenna is:
   *    offline and not supported return -1,
   *    offline and supported return 0,
   *    otherwise return signal value of the terminal
   *
   * @param connectivityStatus terminal status, online | offline
   * @param flSignalQuality  signal value
   * @returns
   */
  export const getSignalQuality = (
    connectivityStatus: string | undefined,
    flSignalQuality: number | null | undefined,
  ) => {
    if (isNil(flSignalQuality)) {
      return flSignalQuality;
    }
    const signalStrength =
      connectivityStatus === 'active' || connectivityStatus === 'online'
        ? flSignalQuality
        : flSignalQuality === -1
        ? -1
        : 0;

    return signalStrength;
  };

  export const getTxPowerDbText = (txPower: number): string => {
    return `Tx Power: ${getTxPowerDbDisplayValue(txPower)}`;
  };

  export const getTxPowerDbDisplayValue = (txPower: number): string => {
    return `${valueOrPlaceholder(txPower, (value: string) => value + ' dBm')}`;
  };

  export const getSiteIdText = (siteId: string): string => {
    return `Site ID: ${valueOrPlaceholder(siteId)}`;
  };

  export const uptimeFormatter = (uptimeSeconds: number) => {
    if (isNil(uptimeSeconds) || uptimeSeconds < 0) return EMPTY_FIELD_STRING_PLACEHOLDER;

    const days = Math.floor(uptimeSeconds / SECONDS_PER_DAY);
    const hours = Math.floor((uptimeSeconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
    const minutes = Math.floor((uptimeSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);

    let result = '';
    if (days > 0) result += `${days}d `;
    if (hours > 0 || days > 0) result += `${hours}h `;
    result += `${minutes}m`;

    return result.trim();
  };

/*
 * Get the SLA value for a given CIR and MIR
 * @param cir - lower bound sla value in kbps
 * @param mir - upper bound sla value in kbps
 * @returns formatted sla value
 * @example getGxSla(1000, 2000) => "1 Mbps to 2 Mbps"
 */
export const getGxSla = (cir: number | undefined, mir: number | undefined) => {
  const cirValue = valueOrPlaceholder(cir, (value: number) => kiloToMega(value) + ' Mbps');
  const mirValue = valueOrPlaceholder(mir, (value: number) => kiloToMega(value) + ' Mbps');

  return `${cirValue} to ${mirValue}`;
};

/**
 * Get the formatter function for adding commas to number values.
 * @param suffix the suffix to be added after the fixed value.
 */
export const toCommaSeparatedString = (suffix: string = '') => {
  return (val: number | undefined | null) => {
    if (!isNil(val)) {
      return `${val.toLocaleString()}${suffix}`;
    }
    return EMPTY_FIELD_STRING_PLACEHOLDER;
  };
};